import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { useHelpModal } from "./HelpModalContext";
import Markdown from "react-markdown";
import {
  attachmentsMarkdown,
  generalMarkdown,
  overviewMarkdown,
  reviewMarkdown,
  sovMarkdown,
  underwritingMarkdown,
} from "./markdown";
import styles from "./HelpModal.module.css";
import { sleepMs } from "../../util";

type SectionName =
  | "overview"
  | "review"
  | "underwriting"
  | "general"
  | "sov"
  | "attachments";

interface Section {
  id: SectionName;
  title: string;
  content: string;
}

interface TableOfContentsProps {
  sections: Section[];
  activeSection: SectionName;
  onClickSection: (sectionId: SectionName) => void;
}

interface ContentWithTOCProps {
  sections: Section[];
  children?: React.ReactNode;
}

// Table of Contents Component
const TableOfContents: React.FC<TableOfContentsProps> = ({
  sections,
  activeSection,
  onClickSection,
}) => {
  return (
    <div className={styles.tableOfContents}>
      <nav aria-label="Table of contents">
        <ul className={styles.tocList}>
          {sections.map((section) => (
            <li
              key={section.id}
              className={`${styles.tocItem} ${
                activeSection === section.id ? styles.active : ""
              }`}
            >
              <a
                href={`#${section.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  onClickSection(section.id);
                }}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

const ContentWithTOC: React.FC<ContentWithTOCProps> = ({ sections }) => {
  const [activeSection, setActiveSection] = useState<SectionName>("general");
  const isAutoScrolling = useRef(false);
  const sectionsRef = useRef<Map<SectionName, HTMLElement | null>>(new Map());
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const sectionElements = new Map<SectionName, HTMLElement | null>();
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        sectionElements.set(section.id, element);
      }
    });

    sectionsRef.current = sectionElements;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isAutoScrolling.current) {
          return;
        }

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.id) {
            setActiveSection(entry.target.id as SectionName);
          }
        });
      },
      {
        rootMargin: "-100px 0px -70% 0px",
        threshold: 0,
      }
    );
    sectionElements.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => {
      sectionElements.forEach((element) => {
        if (element) observer.unobserve(element);
      });
    };
  }, [sections]);

  const scrollToSection = (sectionId: SectionName) => {
    const element = sectionsRef.current.get(sectionId);
    if (element) {
      isAutoScrolling.current = true;
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
      sleepMs(1000).then(() => (isAutoScrolling.current = false));
    }
  };

  return (
    <div className={styles.contentWithToc}>
      <div className={styles.tocSidebar}>
        <TableOfContents
          sections={sections}
          activeSection={activeSection}
          onClickSection={scrollToSection}
        />
      </div>
      <div className={styles.mainContent} ref={contentRef}>
        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className={styles.contentSection}
          >
            <Markdown>{section.content}</Markdown>
          </section>
        ))}
      </div>
    </div>
  );
};

export const HelpModal: React.FC = () => {
  const { visible, hideHelpModal } = useHelpModal();

  const sections: Section[] = [
    {
      id: "overview",
      title: "Overview",
      content: overviewMarkdown,
    },
    {
      id: "review",
      title: "Review Tab",
      content: reviewMarkdown,
    },
    {
      id: "underwriting",
      title: "Underwriting Tab",
      content: underwritingMarkdown,
    },
    {
      id: "general",
      title: "General Tab",
      content: generalMarkdown,
    },
    {
      id: "sov",
      title: "SOV Tab",
      content: sovMarkdown,
    },
    {
      id: "attachments",
      title: "Attachments Tab",
      content: attachmentsMarkdown,
    },
  ];

  return (
    <Dialog
      header="Help"
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => hideHelpModal && hideHelpModal()}
    >
      <ContentWithTOC sections={sections} />
    </Dialog>
  );
};
